export const rorosKeys = {
  launchpad: { status: ['roros', 'launchpad', 'status'] as const },
  /** Strategic Tree view — useRorosTree. Partial match invalidates all variants (with/without work items). */
  tree: {
    all: ['roros', 'tree'] as const,
  },
  domains: {
    all: ['roros', 'domains'] as const,
    detail: (id: string) => ['roros', 'domains', id] as const,
    settings: (id: string) => ['roros', 'domains', id, 'settings'] as const,
  },
  actors: {
    all: ['roros', 'actors'] as const,
    detail: (id: string) => ['roros', 'actors', id] as const,
    consultations: ['roros', 'actors', 'consultations'] as const,
    consultation: (id: string) => ['roros', 'actors', 'consultations', id] as const,
    readinessAssessments: (actorId: string) => ['roros', 'actors', actorId, 'readiness-assessments'] as const,
    /** F-Meeting-1 Wave 1: actor persona configuration */
    actorPersona: (actorId: string) => ['roros', 'actors', actorId, 'persona'] as const,
    /** Wave B AC-MOD-2: org-level moderator default pointers */
    orgModeratorDefaults: ['roros', 'actors', 'org-moderator-defaults'] as const,
  },
  outcomes: {
    byDomain: (domainId: string) => ['roros', 'outcomes', domainId] as const,
    detail: (domainId: string, id: string) => ['roros', 'outcomes', domainId, id] as const,
  },
  metrics: {
    byDomain: (domainId: string) => ['roros', 'metrics', domainId] as const,
    allForOrg: ['roros', 'metrics', 'all'] as const,
    detail: (domainId: string, id: string) => ['roros', 'metrics', domainId, id] as const,
    outcomes: (domainId: string, id: string) => ['roros', 'metrics', domainId, id, 'outcomes'] as const,
    history: (domainId: string, id: string) => ['roros', 'metrics', domainId, id, 'history'] as const,
  },
  principles: {
    byDomain: (domainId: string) => ['roros', 'principles', domainId] as const,
    detail: (domainId: string, id: string) => ['roros', 'principles', domainId, id] as const,
    /** J7-W4 task 6b — cross-domain listing for the new PrinciplesPage. */
    allForOrg: ['roros', 'principles', 'all'] as const,
  },
  routineArtifacts: {
    all: ['roros', 'routine-artifacts', 'all'] as const,
    byDomain: (domainId: string) => ['roros', 'routine-artifacts', domainId] as const,
    detail: (domainId: string, id: string) => ['roros', 'routine-artifacts', domainId, id] as const,
  },
  initiatives: {
    byDomain: (domainId: string) => ['roros', 'initiatives', domainId] as const,
    detail: (domainId: string, id: string) => ['roros', 'initiatives', domainId, id] as const,
    instances: (domainId: string, id: string) => ['roros', 'initiatives', domainId, id, 'instances'] as const,
  },
  knowledge: {
    byDomain: (domainId: string) => ['roros', 'knowledge', domainId] as const,
    detail: (domainId: string, id: string) => ['roros', 'knowledge', domainId, id] as const,
    system: ['roros', 'knowledge', 'system'] as const,
    /** J7-W4 task 6a — items at or near review_due_at deadline. */
    dueForReview: (withinDays: number) => ['roros', 'knowledge', 'due-for-review', withinDays] as const,
    /** AC-K-8: persona pin-deny bindings list */
    personaPinDeny: (personaId: string, mode?: string) => ['roros', 'knowledge', 'persona', personaId, 'pin-deny', mode ?? 'all'] as const,
    /** AC-K-8: reachable-count for a persona */
    personaReachableCount: (personaId: string) => ['roros', 'knowledge', 'persona', personaId, 'reachable-count'] as const,
  },
  learning: {
    byDomain: (domainId: string) => ['roros', 'learning', domainId] as const,
    detail: (domainId: string, id: string) => ['roros', 'learning', domainId, id] as const,
  },
  controlTower: {
    workItems: ['roros', 'control-tower', 'work-items'] as const,
    workItem: (id: string) => ['roros', 'control-tower', 'work-items', id] as const,
    history: (id: string) => ['roros', 'control-tower', 'work-items', id, 'history'] as const,
    wipStatus: ['roros', 'control-tower', 'wip-status'] as const,
    overview: ['roros', 'control-tower', 'overview'] as const,
    verificationInbox: ['roros', 'control-tower', 'verification-inbox'] as const,
    /** J7-W5 task 6c-1: subtree of children for a parent work item. */
    workItemTree: (id: string) => ['roros', 'control-tower', 'work-items', id, 'tree'] as const,
    /** J7-W5 task 6c-1: aggregate counts of children for a parent. */
    workItemRollup: (id: string) => ['roros', 'control-tower', 'work-items', id, 'rollup'] as const,
    /** v1.1.20: full iteration record (deliverable versions + paired QC reports). */
    workItemIterations: (id: string) => ['roros', 'control-tower', 'work-items', id, 'iterations'] as const,
  },
  /** J7-W5 task 6c-2: persisted filter sets ("saved views"). */
  savedViews: {
    list: (scope: string) => ['roros', 'saved-views', scope] as const,
  },
  decisions: {
    all: ['roros', 'decisions'] as const,
    detail: (id: string) => ['roros', 'decisions', id] as const,
    methods: ['roros', 'decisions', 'methods'] as const,
    latency: ['roros', 'decisions', 'latency'] as const,
  },
  quality: {
    reports: ['roros', 'quality', 'reports'] as const,
    report: (id: string) => ['roros', 'quality', 'reports', id] as const,
    ftt: ['roros', 'quality', 'ftt'] as const,
    fttDomain: (domainId: string) => ['roros', 'quality', 'ftt', domainId] as const,
  },
  routines: {
    runs: (routineId: string) => ['roros', 'routines', routineId, 'runs'] as const,
    run: (runId: string) => ['roros', 'routines', 'runs', runId] as const,
    runPackage: (runId: string) => ['roros', 'routines', 'runs', runId, 'package'] as const,
  },
  cadence: {
    schedules: ['roros', 'cadence', 'schedules'] as const,
    reviews: ['roros', 'cadence', 'reviews'] as const,
    review: (id: string) => ['roros', 'cadence', 'reviews', id] as const,
  },
  governance: {
    constitution: ['roros', 'governance', 'constitution'] as const,
    decisionRights: ['roros', 'governance', 'decision-rights'] as const,
    explorationZones: ['roros', 'governance', 'exploration-zones'] as const,
    explorationZone: (id: string) => ['roros', 'governance', 'exploration-zones', id] as const,
    sourceOfTruth: ['roros', 'governance', 'source-of-truth'] as const,
    // J7-W2 — Budget Ledger viewer key (per-operator + per-contract rollups).
    budgetLedger: (actorId?: string | null, days?: number | null) =>
      ['roros', 'governance', 'budget-ledger', actorId ?? null, days ?? null] as const,
  },
  delegations: {
    all: ['roros', 'delegations'] as const,
    detail: (id: string) => ['roros', 'delegations', id] as const,
  },
  visibility: {
    entity: (entityType: string, entityId: string) => ['roros', 'visibility', entityType, entityId] as const,
  },
  roleContracts: {
    all: ['roros', 'role-contracts'] as const,
    templates: ['roros', 'role-contracts', 'templates'] as const,
    detail: (id: string) => ['roros', 'role-contracts', id] as const,
  },
  skills: {
    all: ['roros', 'skills'] as const,
    matrix: ['roros', 'skills', 'matrix'] as const,
    detail: (id: string) => ['roros', 'skills', id] as const,
    attachments: (id: string) => ['roros', 'skills', id, 'attachments'] as const,
    versions: (id: string) => ['roros', 'skills', id, 'versions'] as const,
    version: (id: string, versionId: string) => ['roros', 'skills', id, 'versions', versionId] as const,
  },
  agents: {
    all: ['roros', 'agents'] as const,
    detail: (id: string) => ['roros', 'agents', id] as const,
  },
  agentMemory: {
    all: ['roros', 'agent-memory'] as const,
    detail: (id: string) => ['roros', 'agent-memory', id] as const,
  },
  taskDelegations: {
    all: ['roros', 'task-delegations'] as const,
    detail: (id: string) => ['roros', 'task-delegations', id] as const,
  },
  observability: {
    health: ['roros', 'observability', 'health'] as const,
    domainHealth: (domainId: string) => ['roros', 'observability', 'health', domainId] as const,
    ceoScorecard: ['roros', 'observability', 'scorecard', 'ceo'] as const,
    domainScorecard: (domainId: string) => ['roros', 'observability', 'scorecard', domainId] as const,
    trends: ['roros', 'observability', 'trends'] as const,
    mura: ['roros', 'observability', 'mura'] as const,
  },
  overarching: {
    companyProfile: ['roros', 'overarching', 'company-profile'] as const,
    all: ['roros', 'overarching', 'all'] as const,
    completion: ['roros', 'overarching', 'completion'] as const,
    coreValues: ['roros', 'overarching', 'core-values'] as const,
    coreValue: (id: string) => ['roros', 'overarching', 'core-values', id] as const,
    companyPrinciples: ['roros', 'overarching', 'company-principles'] as const,
    companyPrinciple: (id: string) => ['roros', 'overarching', 'company-principles', id] as const,
    usps: ['roros', 'overarching', 'usps'] as const,
    usp: (id: string) => ['roros', 'overarching', 'usps', id] as const,
    targetMarkets: ['roros', 'overarching', 'target-markets'] as const,
    targetMarket: (id: string) => ['roros', 'overarching', 'target-markets', id] as const,
    personas: ['roros', 'overarching', 'personas'] as const,
    persona: (id: string) => ['roros', 'overarching', 'personas', id] as const,
    swtItems: ['roros', 'overarching', 'swt-items'] as const,
    swtItem: (id: string) => ['roros', 'overarching', 'swt-items', id] as const,
    // visionGoals / visionGoal keys removed: roros_vision_goals table dropped in migration 381.
    icps: ['roros', 'overarching', 'icps'] as const,
    icp: (id: string) => ['roros', 'overarching', 'icps', id] as const,
    icpPersonaLinks: ['roros', 'overarching', 'icp-persona-links'] as const,
    icpPersonaLink: (id: string) => ['roros', 'overarching', 'icp-persona-links', id] as const,
    fieldRegistry: ['roros', 'overarching', 'field-registry'] as const,
    fieldRegistryMatch: (text: string, locale: string) =>
      ['roros', 'overarching', 'field-registry', 'match', locale, text] as const,
  },
  /** Unified Inbox (Packet 1) */
  inbox: {
    list: (params?: Record<string, string>) => ['roros', 'inbox', 'list', params] as const,
  },
} as const;
